import { useState } from 'react';
import { Alert, Button, CircularProgress, Snackbar } from '@mui/material';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { useTranslation } from 'react-i18next';

interface ExportButtonProps {
  /** Writes and downloads the file; resolves with its name. */
  onExport: () => Promise<unknown>;
  label?: string;
  disabled?: boolean;
}

const errorMessage = (error: unknown): string | null => {
  const message = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  return typeof message === 'string' ? message : null;
};

/** "Excel'e aktar" with progress and a result message. */
const ExportButton = ({ onExport, label, disabled }: ExportButtonProps) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleClick = async () => {
    setBusy(true);
    try {
      await onExport();
      setResult({ severity: 'success', message: t('export.done') });
    } catch (error) {
      console.error('Export failed:', error);
      setResult({
        severity: 'error',
        message: errorMessage(error) ?? t('export.failed')
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleClick}
        disabled={busy || disabled}
        startIcon={
          busy ? <CircularProgress size={16} /> : <FileDownloadOutlined />
        }
        sx={{ whiteSpace: 'nowrap' }}
      >
        {busy ? t('export.preparing') : (label ?? t('export.excel'))}
      </Button>
      <Snackbar
        open={result !== null}
        autoHideDuration={6000}
        onClose={() => setResult(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {result ? (
          <Alert
            severity={result.severity}
            variant="filled"
            onClose={() => setResult(null)}
          >
            {result.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default ExportButton;
